/**
 * IPCC SR15 SPM Datasets (Special Report on Global Warming of 1.5°C)
 * Source: IPCC SR15 Summary for Policymakers (2018)
 */

export interface SR15SPM1DataPoint {
  year: number;
  observedGMST: number | null;
  humanForcedGMST: number;
  projectedTrend: number | null;
}

export const SR15_SPM1_DATA: SR15SPM1DataPoint[] = [
  // Historical GMST Anomaly (1850 - 2017)
  { year: 1850, observedGMST: -0.05, humanForcedGMST: 0.02, projectedTrend: null },
  { year: 1880, observedGMST: -0.15, humanForcedGMST: 0.05, projectedTrend: null },
  { year: 1910, observedGMST: -0.25, humanForcedGMST: 0.10, projectedTrend: null },
  { year: 1940, observedGMST: 0.05, humanForcedGMST: 0.22, projectedTrend: null },
  { year: 1970, observedGMST: 0.08, humanForcedGMST: 0.45, projectedTrend: null },
  { year: 1990, observedGMST: 0.45, humanForcedGMST: 0.70, projectedTrend: null },
  { year: 2000, observedGMST: 0.62, humanForcedGMST: 0.85, projectedTrend: null },
  { year: 2010, observedGMST: 0.88, humanForcedGMST: 0.98, projectedTrend: null },
  { year: 2017, observedGMST: 1.00, humanForcedGMST: 1.00, projectedTrend: 1.00 },
  // Projected Linear Trajectory to 1.5°C (~2040)
  { year: 2025, observedGMST: null, humanForcedGMST: 1.16, projectedTrend: 1.16 },
  { year: 2030, observedGMST: null, humanForcedGMST: 1.28, projectedTrend: 1.28 },
  { year: 2035, observedGMST: null, humanForcedGMST: 1.39, projectedTrend: 1.39 },
  { year: 2040, observedGMST: null, humanForcedGMST: 1.50, projectedTrend: 1.50 },
  { year: 2045, observedGMST: null, humanForcedGMST: 1.62, projectedTrend: 1.62 },
  { year: 2050, observedGMST: null, humanForcedGMST: 1.74, projectedTrend: 1.74 }
];

export interface SR15Pathway {
  id: string;
  name: string;
  shortName: string;
  description: string;
  color: string;
  trajectory: {
    year: number;
    netCO2: number; // GtCO2/yr
    cdrVolume: number; // GtCO2/yr negative emissions
    nonCO2Forcing: number; // W/m2
  }[];
}

export const SR15_PATHWAYS: SR15Pathway[] = [
  {
    id: 'p1',
    name: 'Pathway P1: Low Energy Demand (No Overshoot)',
    shortName: 'P1 (Low Demand)',
    description: 'Social, business, and technological innovations result in lower energy demand up to 2050 while living standards rise. Rapid decarbonization without BECCS dependence.',
    color: '#06b6d4',
    trajectory: [
      { year: 2010, netCO2: 38, cdrVolume: 0, nonCO2Forcing: 0.45 },
      { year: 2020, netCO2: 40, cdrVolume: 0, nonCO2Forcing: 0.48 },
      { year: 2030, netCO2: 18, cdrVolume: 0, nonCO2Forcing: 0.35 },
      { year: 2040, netCO2: 4, cdrVolume: 0, nonCO2Forcing: 0.22 },
      { year: 2050, netCO2: -2, cdrVolume: -2, nonCO2Forcing: 0.15 },
      { year: 2075, netCO2: -3, cdrVolume: -3, nonCO2Forcing: 0.10 },
      { year: 2100, netCO2: -2, cdrVolume: -2, nonCO2Forcing: 0.08 }
    ]
  },
  {
    id: 'p2',
    name: 'Pathway P2: Sustainability Focus (No/Limited Overshoot)',
    shortName: 'P2 (Sustainability)',
    description: 'Broad focus on sustainability including energy intensity, human well-being, international cooperation, and land protection. Limited reliance on BECCS.',
    color: '#10b981',
    trajectory: [
      { year: 2010, netCO2: 38, cdrVolume: 0, nonCO2Forcing: 0.45 },
      { year: 2020, netCO2: 40, cdrVolume: 0, nonCO2Forcing: 0.48 },
      { year: 2030, netCO2: 20, cdrVolume: -1, nonCO2Forcing: 0.38 },
      { year: 2040, netCO2: 6, cdrVolume: -4, nonCO2Forcing: 0.26 },
      { year: 2050, netCO2: 0, cdrVolume: -6, nonCO2Forcing: 0.18 },
      { year: 2075, netCO2: -4, cdrVolume: -7, nonCO2Forcing: 0.12 },
      { year: 2100, netCO2: -3, cdrVolume: -5, nonCO2Forcing: 0.10 }
    ]
  },
  {
    id: 'p3',
    name: 'Pathway P3: Middle-of-the-Road (Limited Overshoot)',
    shortName: 'P3 (Middle-of-Road)',
    description: 'Societal and technological development follows historical patterns. Decarbonization achieved mainly by changing energy supply with moderate BECCS.',
    color: '#f59e0b',
    trajectory: [
      { year: 2010, netCO2: 38, cdrVolume: 0, nonCO2Forcing: 0.45 },
      { year: 2020, netCO2: 40, cdrVolume: 0, nonCO2Forcing: 0.48 },
      { year: 2030, netCO2: 26, cdrVolume: -2, nonCO2Forcing: 0.42 },
      { year: 2040, netCO2: 12, cdrVolume: -8, nonCO2Forcing: 0.32 },
      { year: 2050, netCO2: 0, cdrVolume: -12, nonCO2Forcing: 0.24 },
      { year: 2075, netCO2: -6, cdrVolume: -14, nonCO2Forcing: 0.16 },
      { year: 2100, netCO2: -5, cdrVolume: -12, nonCO2Forcing: 0.12 }
    ]
  },
  {
    id: 'p4',
    name: 'Pathway P4: Resource-Intensive (High Overshoot)',
    shortName: 'P4 (High Overshoot)',
    description: 'Resource and energy-intensive development. Emissions continue to mid-century followed by massive reliance on CDR/BECCS to achieve net zero by 2070.',
    color: '#ef4444',
    trajectory: [
      { year: 2010, netCO2: 38, cdrVolume: 0, nonCO2Forcing: 0.45 },
      { year: 2020, netCO2: 40, cdrVolume: 0, nonCO2Forcing: 0.48 },
      { year: 2030, netCO2: 39, cdrVolume: -1, nonCO2Forcing: 0.50 },
      { year: 2040, netCO2: 30, cdrVolume: -6, nonCO2Forcing: 0.45 },
      { year: 2050, netCO2: 15, cdrVolume: -15, nonCO2Forcing: 0.38 },
      { year: 2075, netCO2: -8, cdrVolume: -22, nonCO2Forcing: 0.25 },
      { year: 2100, netCO2: -10, cdrVolume: -20, nonCO2Forcing: 0.18 }
    ]
  }
];

export interface SR15RiskSystem {
  id: string;
  name: string;
  category: 'rfc' | 'system';
  moderateThreshold: number; // °C for yellow
  highThreshold: number;     // °C for red
  veryHighThreshold: number; // °C for purple
  description: string;
}

export const SR15_RISK_SYSTEMS: SR15RiskSystem[] = [
  { id: 'rfc1', name: 'RFC1: Unique & Threatened Systems', category: 'rfc', moderateThreshold: 0.7, highThreshold: 1.2, veryHighThreshold: 1.8, description: 'Coral reefs, mountain glaciers, Arctic ice, biodiversity hotspots' },
  { id: 'rfc2', name: 'RFC2: Extreme Weather Events', category: 'rfc', moderateThreshold: 0.9, highThreshold: 1.5, veryHighThreshold: 2.3, description: 'Heatwaves, heavy rainfall, coastal flooding, drought' },
  { id: 'rfc3', name: 'RFC3: Distribution of Impacts', category: 'rfc', moderateThreshold: 1.1, highThreshold: 1.7, veryHighThreshold: 2.5, description: 'Disproportionate risks to vulnerable & low-income populations' },
  { id: 'rfc4', name: 'RFC4: Global Aggregate Impacts', category: 'rfc', moderateThreshold: 1.3, highThreshold: 2.1, veryHighThreshold: 3.0, description: 'Global economy, global biodiversity loss, market impacts' },
  { id: 'rfc5', name: 'RFC5: Large-Scale Singular Events', category: 'rfc', moderateThreshold: 1.5, highThreshold: 2.2, veryHighThreshold: 2.8, description: 'Ice sheet collapse (Greenland/WAIS), AMOC ocean circulation shutdown' },
  { id: 'corals', name: 'Warm-Water Coral Reefs', category: 'system', moderateThreshold: 0.6, highThreshold: 1.0, veryHighThreshold: 1.5, description: '70-90% loss at 1.5°C; >99% loss at 2.0°C' },
  { id: 'arctic', name: 'Arctic Summer Sea Ice', category: 'system', moderateThreshold: 1.0, highThreshold: 1.8, veryHighThreshold: 2.2, description: 'Ice-free summer 1 in 100 yrs at 1.5°C vs 1 in 10 yrs at 2.0°C' },
  { id: 'coastal', name: 'Coastal Flooding & Sea Level', category: 'system', moderateThreshold: 0.8, highThreshold: 1.5, veryHighThreshold: 2.2, description: '10 million fewer people exposed to sea level rise at 1.5°C vs 2.0°C' },
  { id: 'crops', name: 'Crop Yields (Maize, Rice, Wheat)', category: 'system', moderateThreshold: 1.2, highThreshold: 1.9, veryHighThreshold: 2.6, description: 'Significantly lower yield reductions & food security risks at 1.5°C' }
];

export interface SR15SDGInteraction {
  sdgId: number;
  sdgName: string;
  energySupplyScore: number; // -3 (tradeoff) to +3 (synergy)
  energyDemandScore: number;
  landOceanScore: number;
}

export const SR15_SDG_INTERACTIONS: SR15SDGInteraction[] = [
  { sdgId: 1, sdgName: 'SDG 1: No Poverty', energySupplyScore: 2, energyDemandScore: 3, landOceanScore: 1 },
  { sdgId: 2, sdgName: 'SDG 2: Zero Hunger', energySupplyScore: 1, energyDemandScore: 2, landOceanScore: -2 },
  { sdgId: 3, sdgName: 'SDG 3: Good Health & Well-being', energySupplyScore: 3, energyDemandScore: 3, landOceanScore: 2 },
  { sdgId: 6, sdgName: 'SDG 6: Clean Water & Sanitation', energySupplyScore: 1, energyDemandScore: 2, landOceanScore: -1 },
  { sdgId: 7, sdgName: 'SDG 7: Affordable & Clean Energy', energySupplyScore: 3, energyDemandScore: 3, landOceanScore: 1 },
  { sdgId: 8, sdgName: 'SDG 8: Decent Work & Economic Growth', energySupplyScore: 2, energyDemandScore: 2, landOceanScore: 1 },
  { sdgId: 12, sdgName: 'SDG 12: Responsible Consumption', energySupplyScore: 2, energyDemandScore: 3, landOceanScore: 2 },
  { sdgId: 13, sdgName: 'SDG 13: Climate Action', energySupplyScore: 3, energyDemandScore: 3, landOceanScore: 3 },
  { sdgId: 14, sdgName: 'SDG 14: Life Below Water', energySupplyScore: 2, energyDemandScore: 2, landOceanScore: 3 },
  { sdgId: 15, sdgName: 'SDG 15: Life on Land', energySupplyScore: 1, energyDemandScore: 2, landOceanScore: 2 }
];
